/**
 * Tutorial content — the ONLY place tutorial copy lives (PRD FR-7).
 *
 * Voice: terse survivor-log, second person, NO exclamation marks (art spec §8).
 * Strings stay ≤ ~70 chars/line for i18n headroom. Numbers render in mono
 * (chips use --font-family-mono at the component layer).
 *
 * Step shape:
 *   id        — 1..8, strictly sequential (PRD: one-action blocker forbids parallel)
 *   kind      — 'welcome' | 'spotlight' | 'panel' | 'completion'
 *   beats     — sequenced sub-highlights, ONE salient at a time (art spec §3)
 *   beat.target — data-tutorial hook name; null => centered panel fallback
 *   beat.cta  — { label, action, to? } where action is one of:
 *                 'ack'      — acknowledge the step server-side (intro steps 1–2
 *                              ONLY; the step-complete endpoint is intro-only)
 *                 'navigate' — router push to `to`
 *                 'click'    — dispatch a real click on the spotlit target
 *                 'wait'     — milestone pending; overlay collapses to the pill
 *   actionType — which Player.current_action this step waits on ('scavenging' |
 *                'gathering' | 'crafting'); drives the run-in-progress pill
 *   milestones — server-detected keys (FR-2: never fired from tutorial UI clicks)
 *   demo/real  — documented pacing for timed steps (PRD NFR: both values shown)
 */

export const TUTORIAL_STEPS = [
    {
        id: 1,
        slug: 'welcome',
        kind: 'welcome',
        title: 'The log begins',
        milestones: [],
        panels: [
            {
                title: 'You survived',
                body: 'The collapse took everything else. You walked out with a pulse and a bunker key.',
            },
            {
                title: 'This bunker is home',
                body: 'Rooms produce while you are away. Rest here. Nothing behind these walls can kill you.',
            },
            {
                title: 'The outside has gear',
                body: 'Keep it if you extract. Lose it if you die. Every run is a bet — you set the size.',
            },
        ],
    },
    {
        id: 2,
        slug: 'shelter-orientation',
        kind: 'spotlight',
        title: 'Home base',
        milestones: [],
        beats: [
            {
                target: 'stats-bar',
                title: 'Your vitals',
                body: 'Health, energy, hunger. A zero in any of these ends the story — watch them first.',
                cta: { label: 'Next', action: 'next-beat' },
            },
            {
                // Rooms are not on the Shelter view — null target renders this beat
                // as a centered panel (tutorialTargets fallback, no dangling arrow).
                target: null,
                title: 'Rooms work while you rest',
                body: 'Production runs on its own and never blocks you. Start a job, then go do anything else.',
                cta: { label: 'Got it', action: 'ack' },
            },
        ],
    },
    {
        id: 3,
        slug: 'first-run',
        kind: 'spotlight',
        title: 'First run',
        milestones: ['first_run_completed'],
        waiting: true,
        actionType: 'scavenging',
        real: '≈10 min (real)',
        demo: '≈2 min (demo)',
        beats: [
            {
                target: 'nav-scavenging',
                title: 'Scavenging',
                body: 'Runs are how gear enters your hands. One action at a time — nothing else starts while a run is out.',
                cta: { label: 'Open Scavenging', action: 'navigate', to: '/scavenging' },
            },
            {
                target: 'map-card-1',
                title: 'Start close',
                body: 'Map 1 is the shortest walk home. Less time outside, less to lose. Gear up and launch.',
                cta: { label: 'Open the map', action: 'click' },
            },
        ],
    },
    {
        id: 4,
        slug: 'first-loot',
        kind: 'panel',
        title: 'What you brought back',
        milestones: ['first_loot_inspected'],
        beats: [
            {
                target: null,
                title: 'Loot on extraction',
                body: 'Variants run DESTROYED to EXCEPTIONAL — same item, different roll. The codex logs what you have seen.',
                cta: { label: 'Open your inventory', action: 'navigate', to: '/shelter' },
            },
        ],
        // Milestone (first_loot_inspected) fires when the player opens the
        // inventory — after the CTA the overlay collapses to the pill and waits.
        waitsAfterBeats: true,
    },
    {
        id: 5,
        slug: 'equip-and-earn',
        kind: 'spotlight',
        title: 'Gear up, cash in',
        milestones: ['first_item_equipped', 'first_currency_earned'],
        waitsAfterBeats: true,
        beats: [
            {
                target: 'inventory-grid',
                title: 'First power, first currency',
                body: 'Equip your best find. Sell one starting material. Power you keep, currency you spend.',
                cta: { label: 'Got it', action: 'ack-beat' },
            },
        ],
    },
    {
        id: 6,
        slug: 'first-gathering',
        kind: 'spotlight',
        title: 'First gathering',
        milestones: ['first_gathering_completed'],
        waiting: true,
        actionType: 'gathering',
        real: '≈5 min (real)',
        demo: '≈1 min (demo)',
        beats: [
            {
                target: 'nav-gatherings',
                title: 'Gatherings',
                body: 'Mining and woodcutting cost stamina and take time. They tick on their own — you keep playing.',
                cta: { label: 'Open Gatherings', action: 'navigate', to: '/gathering' },
            },
            {
                target: 'gathering-mining',
                title: 'Mining',
                body: 'Stamina in, ore out, on a timer. Check the cost before you swing.',
                cta: { label: 'Next', action: 'next-beat' },
            },
            {
                target: 'gathering-woodcutting',
                title: 'Or woodcutting',
                body: 'Same lesson, different material. Pick whichever the bunker needs more.',
                cta: { label: 'Pick one and start it', action: 'click' },
            },
        ],
    },
    {
        id: 7,
        slug: 'first-craft',
        kind: 'spotlight',
        title: 'First craft',
        milestones: ['first_craft_completed'],
        waiting: true,
        actionType: 'crafting',
        real: '≈3 min (real)',
        demo: '≈40 s (demo)',
        beats: [
            {
                target: 'nav-crafting',
                title: 'Crafting',
                body: 'Stations turn materials into gear. Jobs queue and finish on their own — a toast lands when done.',
                cta: { label: 'Open Crafting', action: 'navigate', to: '/crafting' },
            },
            {
                target: 'station-1',
                title: 'Unlock, then queue',
                body: 'Locked bench? Its card shows the cost — click it and pay. Unlocked? Queue a recipe you can afford.',
                cta: { label: 'Use the bench', action: 'click' },
            },
        ],
    },
    {
        id: 8,
        slug: 'first-vendor',
        kind: 'spotlight',
        title: 'First trade',
        milestones: ['first_vendor_purchase'],
        waitsAfterBeats: true,
        beats: [
            {
                target: 'nav-vendors',
                title: 'Vendors',
                body: 'Nina the Collector trades at level one. Trust grows with every deal — prices move in your favor.',
                cta: { label: 'Open Vendors', action: 'navigate', to: '/vendors' },
            },
            {
                target: 'vendor-nina',
                title: 'First purchase',
                body: 'Buy one cheap item with what you earned. That closes the loop: run, loot, equip, work, craft, trade.',
                cta: { label: 'Got it', action: 'ack-beat' },
            },
        ],
    },
]

export const TOTAL_STEPS = TUTORIAL_STEPS.length

export const TUTORIAL_COPY = {
    logChip: (step) => `LOG ${step}/${TOTAL_STEPS}`,
    pillWaiting: 'in progress — the tutorial waits',
    // Per-action waiting copy (PRD risk note: the wait must stay legible).
    pillWaitingByAction: {
        scavenging: 'run in progress — the tutorial waits',
        gathering: 'gathering in progress — the tutorial waits',
        crafting: 'job in progress — the tutorial waits',
    },
    pillCollapsed: 'resume the log',
    skip: 'Skip tutorial',
    skipConfirm: 'Skip the tutorial?',
    skipConfirmDetail: 'You can restart it anytime from your profile. Milestones already earned stay earned.',
    skipToast:
        'Tutorial skipped — restart anytime: Profile → Restart tutorial.',
    resumeAria: 'Resume tutorial',
    collapseAria: 'Collapse tutorial',
    stepAnnounce: (step, title) => `Step ${step} of ${TOTAL_STEPS}: ${title}`,
    blockedClick: 'Finish this beat first — or skip the tutorial.',
    restart: 'Restart tutorial',
    restartHiddenHint: 'Tutorial already complete — nothing to restart.',
    completion: {
        title: 'Tutorial complete',
        body: 'The loop is yours now: rest, run, extract, craft, trade. Your starter bundle is in the inventory.',
        bundleLabel: 'Starter bundle',
        pointers: [
            { label: 'Daily Rewards', to: '/daily-rewards', note: 'Check in every day' },
            { label: 'Achievements', to: '/achievements', note: 'Long-term goals' },
            { label: 'Codex', to: '/codex', note: 'Everything you have seen' },
        ],
        cta: 'Back to the bunker',
    },
    welcomeFooter: 'You can skip from any step — no penalty, ever.',
    welcomeCta: 'Begin the log',
    // §10 death-recovery beat (Boris ruling 2026-10-03: a death IS a completed
    // run — first_run_completed fires, server advances, no re-grant needed).
    // Pure FE: shown on the run-death screen during the tutorial window only.
    recovery: {
        title: 'The bunker provides',
        body: 'You lost what you carried — that is the rule outside. Whatever you left in the shelter is safe. Gear up and finish the log.',
    },
}

/** Milestone key -> step id (client mirror of server detection; server is authoritative). */
export const MILESTONE_TO_STEP = TUTORIAL_STEPS.reduce((acc, step) => {
    step.milestones.forEach((m) => { acc[m] = step.id })
    return acc
}, {})

export function getStep(id) {
    return TUTORIAL_STEPS.find((s) => s.id === id) || null
}
